import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ai, isApiError } from '@repletoryreach/sdk-api';
import { Card, Stack, Text, Title, Button, Group } from '@repletoryreach/sdk-ui';
import { EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { NumberInput, Select, Switch, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { ModelSelect } from './shared';
import { SKILLS_KEY } from './WriteTab';

/** How freely the model writes: three plain choices instead of a temperature. */
const CREATIVITY = [{ value: '0.2', key: 'precise' }, { value: '0.5', key: 'balanced' }, { value: '0.8', key: 'creative' }] as const;

/** A code from a name: "School newsletter" → "school-newsletter". */
const codeFrom = (name: string) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

/**
 * My skills (14 October 2026): the business's own instructions, background and tone, saved once and run from Write
 * or from its own systems with POST /v1/ai/run.
 */
export function SkillsTab() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: SKILLS_KEY, queryFn: () => ai.listSkills() });
  const own = (q.data?.data ?? []).filter((s) => !s.built_in);
  const [editing, setEditing] = useState<ai.AiSkill | 'new' | null>(null);

  const remove = useMutation({
    mutationFn: (s: ai.AiSkill) => ai.deleteSkill(s.id!),
    onSuccess: (_r, s) => { qc.invalidateQueries({ queryKey: SKILLS_KEY }); notifySuccess(t('ai.skills.deleted', { name: s.name }), t('ai.title')); },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('ai.title'))
  });

  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap">
            <Stack gap={2}>
              <Title order={4}>{t('ai.skills.title')}</Title>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('ai.skills.body')}</Text>
            </Stack>
            <Button size="sm" onClick={() => setEditing('new')} data-testid="ai-skill-new">{t('ai.skills.new')}</Button>
          </Group>
          {own.length === 0 && !q.isPending ? (
            <EmptyState title={t('ai.skills.empty')} description={t('ai.skills.emptyHint')} />
          ) : own.map((s) => (
            <div key={s.id} className="rr-ai__skill" data-testid="ai-skill-row">
              <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text role="bodySmall" fw={650}>{s.name} <code>{s.code}</code></Text>
                {s.description && <Text role="caption" c="var(--rr-color-text-muted)">{s.description}</Text>}
              </Stack>
              <Group gap={4} wrap="nowrap">
                <Button size="xs" variant="subtle" onClick={() => setEditing(s)}>{t('action.edit')}</Button>
                <Button size="xs" variant="subtle" color="red" onClick={() => openConfirmModal({
                  title: t('ai.skills.deleteTitle', { name: s.name }),
                  children: <Text role="bodySmall">{t('ai.skills.deleteBody')}</Text>,
                  labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(s)
                })}>{t('action.delete')}</Button>
              </Group>
            </div>
          ))}
        </Stack>
      </Card>
      <SkillDrawer skill={editing} onClose={() => setEditing(null)} />
    </Stack>
  );
}

function SkillDrawer({ skill, onClose }: { skill: ai.AiSkill | 'new' | null; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const existing = skill && skill !== 'new' ? skill : null;
  const [seeded, setSeeded] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', code: '', description: '', instructions: '', context: '', tone: '', model: '', creativity: '0.5', max: 1500 as number | '', kb: false });
  const [codeTouched, setCodeTouched] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const key = skill === 'new' ? 'new' : existing?.id ?? null;
  if (key && seeded !== key) {
    setSeeded(key);
    setErrors({});
    setCodeTouched(!!existing);
    setForm({
      name: existing?.name ?? '', code: existing?.code ?? '', description: existing?.description ?? '',
      instructions: existing?.instructions ?? '', context: existing?.context ?? '', tone: existing?.tone ?? '',
      model: existing?.model ?? '', creativity: String(existing?.temperature ?? 0.5), max: existing?.max_tokens ?? 1500, kb: !!existing?.use_knowledge_base
    });
  }
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () => {
      const body: ai.SaveAiSkillRequest = {
        code: form.code.trim(), name: form.name.trim(), instructions: form.instructions.trim(),
        description: form.description.trim() || null, context: form.context.trim() || null, tone: form.tone.trim() || null,
        model: form.model || null, temperature: Number(form.creativity), max_tokens: typeof form.max === 'number' ? form.max : null,
        use_knowledge_base: form.kb
      };
      return existing ? ai.updateSkill(existing.id!, body) : ai.createSkill(body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SKILLS_KEY });
      notifySuccess(t('ai.skills.saved', { name: form.name.trim() }), t('ai.title'));
      onClose();
    },
    onError: (e) => {
      const body = isApiError(e) ? (e.body as { error?: { field?: string; message?: string } } | null) : null;
      if (body?.error?.field) setErrors({ [body.error.field]: body.error.message ?? '' });
      else if (isApiError(e) && e.code === 'AI_SKILL_EXISTS') setErrors({ code: e.message });
      else notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('ai.title'));
    }
  });

  return (
    <Drawer opened={!!skill} onClose={onClose} title={existing ? existing.name : t('ai.skills.new')} position="right" size="lg">
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Stack gap="md" style={{ padding: 4 }}>
          <TextInput label={t('ai.skills.name')} description={t('ai.skills.nameHint')} value={form.name} required error={errors.name}
            onChange={(e) => { const name = e.currentTarget.value; set({ name, ...(codeTouched ? {} : { code: codeFrom(name) }) }); }} />
          <TextInput label={t('ai.skills.code')} description={t('ai.skills.codeHint')} value={form.code} required error={errors.code}
            onChange={(e) => { setCodeTouched(true); set({ code: e.currentTarget.value }); }} />
          <TextInput label={t('ai.skills.description')} description={t('ai.skills.descriptionHint')} value={form.description}
            onChange={(e) => set({ description: e.currentTarget.value })} />
          <Textarea label={t('ai.skills.instructions')} description={t('ai.skills.instructionsHint')} value={form.instructions}
            minRows={4} autosize required error={errors.instructions} onChange={(e) => set({ instructions: e.currentTarget.value })} />
          <Textarea label={t('ai.skills.context')} description={t('ai.skills.contextHint')} value={form.context}
            minRows={3} autosize error={errors.context} onChange={(e) => set({ context: e.currentTarget.value })} />
          <TextInput label={t('ai.skills.tone')} description={t('ai.skills.toneHint')} value={form.tone}
            onChange={(e) => set({ tone: e.currentTarget.value })} />
          <Select label={t('ai.skills.creativity')} description={t('ai.skills.creativityHint')} value={form.creativity}
            onChange={(e) => set({ creativity: e.currentTarget.value })}
            data={CREATIVITY.map((c) => ({ value: c.value, label: t(`ai.skills.creativity.${c.key}`) }))} />
          <NumberInput label={t('ai.skills.maxTokens')} description={t('ai.skills.maxTokensHint')} value={form.max} min={100} max={8000} step={100}
            error={errors.max_tokens} onChange={(v) => set({ max: v })} />
          <ModelSelect value={form.model} onChange={(model) => set({ model })} />
          <Switch label={t('ai.skills.kb')} description={t('ai.skills.kbHint')} checked={form.kb} onChange={(kb: boolean) => set({ kb })} />
          <Group justify="flex-end">
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={save.isPending} disabled={!form.name.trim() || !form.code.trim() || !form.instructions.trim()}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
